import { LinkOutlined } from "@ant-design/icons";
import { DOCS_URLS, useLocalStorage } from "@p0security/frontend-core";
import { RequestStatuses } from "@p0security/shared/types/request-status";
import {
  Card,
  Flex,
  Skeleton,
  Space,
  Spacing,
  Typography,
} from "@p0security/ui";
import { FindingsContext } from "components/Assessment/contexts/FindingsContext";
import { useInstalledIntegrations } from "components/Assessment/hooks/useInstalledIntegrations";
import { CreateEnvironment } from "components/Assessment/pages/CreateEnvironment";
import { EnvironmentSelector } from "components/Environment/components/EnvironmentSelector";
import { EnvironmentDocsContext } from "components/Environment/contexts/EnvironmentDocsContext";
import { useRequests } from "components/Jit/Requests/hooks/useRequests";
import { useScreen } from "hooks/useScreen";
import { useCallback, useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import { colors } from "styles/variables";

import { ItemChart } from "./components/Inventory/ItemChart";
import { JitPanels } from "./components/Jit/JitPanel";
import { PosturePanel } from "./components/PosturePanel";
import { GrayTitle } from "./components/common/GrayTitle";
import { PanelErrorBoundary } from "./components/common/PanelErrorBoundary";
import { ZeroState } from "./components/common/ZeroState";

const ZERO_STATE_LOCAL_STORAGE_KEY = "dashboard-zero-state";
type StoredZeroState = {
  hideJit: boolean | undefined;
  hideEnvironment: boolean | undefined;
};
export const Dashboard: React.FC<object> = ({}) => {
  const { loading: isFindingsLoading } = useContext(FindingsContext);
  const { hasEnvironments } = useContext(EnvironmentDocsContext);

  // Presence of JiT requests used to determine whether JiT is enabled
  // TODO: this is fragile; we should use integration config instead
  const { loading: isRequestsLoading, hasRequests } = useRequests(
    RequestStatuses,
    { limit: 1 }
  );

  const { xl } = useScreen();

  const [storedZeroState, setStoredZeroState] =
    useLocalStorage<StoredZeroState>(ZERO_STATE_LOCAL_STORAGE_KEY, {
      hideJit: undefined,
      hideEnvironment: undefined,
    });

  const hideJit = useCallback(() => {
    setStoredZeroState({ ...storedZeroState, hideJit: true });
  }, [setStoredZeroState, storedZeroState]);

  const hideEnvironment = useCallback(() => {
    setStoredZeroState({ ...storedZeroState, hideEnvironment: true });
  }, [setStoredZeroState, storedZeroState]);

  const inventoryPanel = (
    <Flex className={xl ? "w-1/2" : "w-full"} vertical>
      <Space size={20}>
        <GrayTitle title={"Inventory"} />
      </Space>
      <PanelErrorBoundary title="Inventory">
        <ItemChart hasEnvironments={hasEnvironments} />
      </PanelErrorBoundary>
    </Flex>
  );

  const posturePanels = (
    <>
      <Flex className="w-full" gap="1em">
        <Flex className={xl ? "w-1/2" : "w-full"} vertical>
          <Space size={20}>
            <GrayTitle title="Posture" />
          </Space>
          <PanelErrorBoundary title="Posture">
            <PosturePanel
              hasEnvironments={hasEnvironments}
              isLoading={isFindingsLoading}
            />
          </PanelErrorBoundary>
        </Flex>
        {xl && inventoryPanel}
      </Flex>
      {!xl && inventoryPanel}
    </>
  );

  const { installed } = useInstalledIntegrations();

  const allowHidingZeroState = useMemo(
    () => hasRequests || hasEnvironments,
    [hasRequests, hasEnvironments]
  );

  if (isFindingsLoading || xl === undefined || isRequestsLoading) {
    return (
      <Space direction="vertical" size={"middle"} className="w-full">
        <Card
          variant="borderless"
          styles={{ body: { background: colors.neutral["10"] } }}
        >
          <Flex className="w-full" gap="1em">
            <Skeleton
              active
              /* eslint-disable-line react/forbid-component-props */ style={{
                padding: "96px",
              }}
              paragraph={{ rows: 4 }}
            />
            <Skeleton
              active
              /* eslint-disable-line react/forbid-component-props */ style={{
                padding: "96px",
              }}
              paragraph={{ rows: 4 }}
            />
          </Flex>
        </Card>
        <Card
          variant="borderless"
          styles={{ body: { background: colors.neutral["10"] } }}
        >
          <Flex className="w-full" gap="1em">
            <Skeleton
              active
              /* eslint-disable-line react/forbid-component-props */ style={{
                padding: "96px",
              }}
              paragraph={{ rows: 4 }}
            />
            <Skeleton
              active
              /* eslint-disable-line react/forbid-component-props */ style={{
                padding: "96px",
              }}
              paragraph={{ rows: 4 }}
            />
          </Flex>
        </Card>
      </Space>
    );
  }
  const innerJitPanel: JSX.Element = (
    <PanelErrorBoundary title="Access Management">
      <JitPanels breakpoint={xl} hasRequests={hasRequests} />
    </PanelErrorBoundary>
  );
  const jitPanels = hasRequests ? (
    <Card
      variant="borderless"
      styles={{ body: { background: colors.neutral["10"] } }}
    >
      {innerJitPanel}
    </Card>
  ) : storedZeroState?.hideJit ? null : (
    <Card
      variant="borderless"
      styles={{ body: { background: colors.neutral["10"] } }}
    >
      <ZeroState
        allowHide={allowHidingZeroState}
        onHide={hideJit}
        cta={
          <div>
            <div>
              <Typography.Text
                /* eslint-disable-line react/forbid-component-props */ style={{
                  fontSize: "large",
                }}
              >
                Set up Access Management.
              </Typography.Text>
            </div>
            <div>
              <Spacing mt="1em">
                <Link
                  to={DOCS_URLS.QUICK_START_CONFIGURE_APPROVER}
                  target="_blank"
                >
                  Docs <LinkOutlined />
                </Link>
              </Spacing>
            </div>
          </div>
        }
        background={innerJitPanel}
      />
    </Card>
  );

  if (!hasEnvironments) {
    return (
      <Space direction="vertical" size={"middle"} className="w-full">
        <PanelErrorBoundary title="Access Management">
          {jitPanels}
        </PanelErrorBoundary>
        {!storedZeroState?.hideEnvironment && (
          <Card
            variant="borderless"
            className="relative"
            styles={{ body: { background: colors.neutral["10"] } }}
          >
            <ZeroState
              allowHide={allowHidingZeroState}
              onHide={hideEnvironment}
              cta={
                <div>
                  <div>
                    <Typography.Text
                      /* eslint-disable-line react/forbid-component-props */ style={{
                        fontSize: "large",
                      }}
                    >
                      Create an environment to get started.
                    </Typography.Text>
                  </div>
                  <Spacing mt="1em">
                    <CreateEnvironment
                      buttonCopy="Create"
                      installed={installed}
                    />
                  </Spacing>
                </div>
              }
              background={posturePanels}
            />
          </Card>
        )}
      </Space>
    );
  }

  return (
    <Space direction="vertical" size={"middle"} className="w-full">
      <Card
        variant="borderless"
        styles={{ body: { background: colors.neutral["10"] } }}
      >
        <EnvironmentSelector />
        {posturePanels}
      </Card>
      <PanelErrorBoundary title="Access Management">
        {jitPanels}
      </PanelErrorBoundary>
    </Space>
  );
};
